import { defineStore } from 'pinia'
import { responseErrors } from '@team-milva/lab-constants'
import { useUserStore } from '@/stores/userStore'
import { reportUnexpectedError } from '@/globals/typescript/utils/reportError'
import {
  customerOwnerApi,
  customerOwnerContext,
} from '@/globals/typescript/utils/api/customerOwnerApi'

/** One generated customer report (Kunderapport), as the API lists it. */
export interface ICustomerReportItem {
  id: string
  /** What the download is saved as — "Kunderapport - {street, postcode city}.pdf". */
  fileName: string
  storagePath: string
  createdAt: number
  /** How many deliveries the report was built from. */
  deliveryCount: number
}

/** What generating returns: the report itself, before the list is read again for its delivery count. */
export type TGeneratedCustomerReport = Pick<ICustomerReportItem, 'id' | 'fileName' | 'storagePath' | 'createdAt'>

export interface IOrderCustomerReports {
  /** False when the caller may not use customer reports on this order (not its creator, nor an account admin). */
  isAvailable: boolean
  reports: ICustomerReportItem[]
  /** At least one delivery is delivered — a report can be generated at all. */
  hasDeliveredDelivery: boolean
  /** A new report would differ from the latest one, or there is none yet. */
  hasChanges: boolean
  /** A delivery was delivered after the latest report was made — as opposed to something in it being edited. */
  hasNewDelivery: boolean
}

/**
 * An order's customer reports. Generation and history go through lab-cloud; the PDF itself is
 * downloaded from Storage like a lab report is (`downloadStorageFile`).
 */
export const useCustomerReportStore = defineStore('customerReport', {
  state: () => ({
    byOrderID: {} as Record<string, IOrderCustomerReports>,
  }),

  actions: {
    /** Loads the order's customer reports and what can be done with them. Safe to call again to refresh. */
    async loadCustomerReports(orderID: string): Promise<void> {
      const owner = customerOwnerContext(useUserStore().currentUser)
      if (!owner) {
        throw new Error('Only a business or insurance customer user can load customer reports')
      }

      const result = await customerOwnerApi(owner, 'get', '/orders/{orderID}/customer-reports', {
        path: { orderID },
      })

      if (!result.success) {
        // "Not found" is how the API says the caller may not use this — hide the section rather than fail.
        if (result.code === responseErrors.NOT_FOUND.code) {
          this.byOrderID[orderID] = {
            isAvailable: false,
            reports: [],
            hasDeliveredDelivery: false,
            hasChanges: false,
            hasNewDelivery: false,
          }
          return
        }
        throw new Error(result.message ?? result.code)
      }

      this.byOrderID[orderID] = { isAvailable: true, ...result.data }
    },

    /**
     * Generates a customer report, or — when nothing it shows has changed — the API hands back the
     * latest one. Returns the report and whether it is new, then refreshes the list.
     */
    async generateCustomerReport(orderID: string): Promise<{ report: TGeneratedCustomerReport; isNew: boolean }> {
      const owner = customerOwnerContext(useUserStore().currentUser)
      if (!owner) {
        throw new Error('Only a business or insurance customer user can generate a customer report')
      }

      const result = await customerOwnerApi(owner, 'post', '/orders/{orderID}/customer-reports', {
        path: { orderID },
      })
      if (!result.success) throw new Error(result.message ?? result.code)

      // The report exists now. Refreshing the list is a separate, best-effort step: if it fails the
      // caller still gets (and can download) the report, and the list catches up on the next load.
      try {
        await this.loadCustomerReports(orderID)
      } catch (error) {
        reportUnexpectedError(error, { operation: 'customer-report-history-refresh', extra: { orderID } })
      }
      return result.data
    },

    resetStore(): void {
      this.byOrderID = {}
    },
  },
})
